import React, { useEffect, useState } from "react"
import MetaTags from "react-meta-tags"
import { connect } from "react-redux"
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Breadcrumb,
} from "antd"
import { EditOutlined } from "@ant-design/icons"
import { withTranslation } from "react-i18next"

// Redux actions
import {
  deleteUSER,
  getUSERs,
  insertUSER,
  resetUSERFlag,
  updateUSER,
} from "../../store/actions"
import "./users.scss"
const { Option } = Select

const Users = ({ t, lstUSERs, error, loading, success, ...props }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [filteredData, setFilteredData] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [form] = Form.useForm()
  const [modalVisible, setModalVisible] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)

  useEffect(() => {
    props.onGetUSERs()
  }, [])

  useEffect(() => {
    // Initialize filteredData when data loads
    setFilteredData(lstUSERs)
    setCurrentPage(1)
  }, [lstUSERs])

  useEffect(() => {
    const filtered = lstUSERs.filter(row => {
      const searchText = searchTerm.toLowerCase()

      // Get all visible values including rendered content
      const valuesToSearch = [
        row.email,
        row.phoneNumber,
        t(row.role), // Translated role
        row.active ? t("Active") : t("Inactive"), // Status text
        row.expiryDate,
        row.firstName,
        row.lastName,
      ]

      return valuesToSearch.some(value =>
        String(value).toLowerCase().includes(searchText)
      )
    })

    setFilteredData(filtered)
    setCurrentPage(1)
  }, [searchTerm, lstUSERs, t])

  // Add this in your JSX before the Table
  const searchInput = (
    <Input.Search
      placeholder={t("Search all columns...")}
      allowClear
      onChange={e => setSearchTerm(e.target.value)}
      style={{ width: 300, marginBottom: 16 }}
    />
  )

  useEffect(() => {
    if (success || error) {
      setTimeout(() => props.onResetUSERFlag(), 3000)
    }
  }, [success, error])

  const columns = [
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Phone", dataIndex: "phoneNumber", key: "phoneNumber" },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: role => t(role),
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: active => (
        <Tag color={active ? "green" : "red"}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      key: "expiryDate",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
        </Space>
      ),
    },
  ]

  const handleSubmit = async values => {
    const userData = {
      ...values,
      expiryDate: values.expiryDate?.format("YYYY-MM-DD"),
    }

    if (editMode && selectedUser) {
      await props.onUpdateUSER({ ...userData, id: selectedUser.id })
    } else {
      await props.onInsertUSER(userData)
    }

    setModalVisible(false)
    form.resetFields()
  }

  const handleEdit = user => {
    setEditMode(true)
    setSelectedUser(user)
    form.setFieldsValue({
      ...user,
      expiryDate: user.expiryDate ? moment(user.expiryDate) : null,
    })
    setModalVisible(true)
  }

  return (
    <div className="page-content">
      <MetaTags>
        <title>User Management | gasUnit</title>
      </MetaTags>

      <>
        <div className="mb-4">
          <Typography.Title level={4}>User Management</Typography.Title>
          <Breadcrumb items={[{ title: "gasUnit" }, { title: "Users" }]} />
        </div>
      </>

      <Card>
        {error && <Alert message={error} type="error" showIcon closable />}
        {success && (
          <Alert message={success} type="success" showIcon closable />
        )}

        {searchInput}
        <div
          style={{
            height: "calc(100vh - 300px)", // Adjust based on your layout
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={filteredData}
            pagination={{
              className: "custom-pagination", // Add custom class
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              onShowSizeChange: (current, size) => {
                setPageSize(size)
                setCurrentPage(1) // Reset to first page when changing page size
              },
              onChange: page => setCurrentPage(page),
              total: filteredData.length,
              style: {
                position: "sticky",
                bottom: 0,
                background: "white",
                marginTop: "auto", // Pushes pagination to bottom
              },
            }}
            scroll={{
              y: "calc(100vh - 380px)", // Adjust based on your container height
            }}
          />
        </div>
      </Card>

      <Modal
        title={`${editMode ? "Edit" : "New"} User`}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          form.resetFields()
          setEditMode(false)
          setSelectedUser(null)
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ role: "Employee", active: true }}
        >
          {!editMode && (
            <>
              <Form.Item
                label="Email"
                name="email"
                rules={[{ required: true, message: "Please input email!" }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: !editMode, message: "Please input password!" },
                ]}
              >
                <Input.Password />
              </Form.Item>
            </>
          )}

          <Form.Item
            label="First Name"
            name="firstName"
            rules={[{ required: true, message: "Please input first name!" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: "Please input last name!" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Phone Number" name="phoneNumber">
            <Input />
          </Form.Item>

          <Form.Item label="Expiry Date" name="expiryDate">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="Role" name="role">
            <Select>
              <Option value="Employee">Employee</Option>
              <Option value="Engineer">Engineer</Option>
              <Option value="Executor">Executor</Option>
              <Option value="Accountant">Accountant</Option>
              <Option value="Administrator">Administrator</Option>
              <Option value="Installer">Installer</Option>
            </Select>
          </Form.Item>

          {editMode && (
            <Form.Item label="Status" name="active">
              <Select>
                <Option value={true}>Active</Option>
                <Option value={false}>Inactive</Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item>
            <Space style={{ float: "right" }}>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                {editMode ? "Update" : "Create"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

const mapStateToProps = ({ USERs }) => ({
  lstUSERs: USERs.lstUSERs,
  error: USERs.error,
  success: USERs.success,
  loading: USERs.loading,
})

const mapDispatchToProps = {
  onGetUSERs: getUSERs,
  onDeleteUSER: deleteUSER,
  onUpdateUSER: updateUSER,
  onInsertUSER: insertUSER,
  onResetUSERFlag: resetUSERFlag,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(Users))
